import React, { useEffect, useState, useMemo, useCallback, MouseEvent, useRef } from 'react';
import { User, Day, IEvent, Position, ApiResponse, Event, MONTHNAMES, HOST } from './types';
import { xhr } from './util';
import { useNavigate } from 'react-router-dom';

export default function DayBlock(
  { user, day, a, b, theme, eventId, setEventOrigin, setEvent, setCalendar, setUser }: 
  { user: User, day: Day, a: number, b: number, eventId: string,
      theme: string,
    setEventOrigin: React.Dispatch<React.SetStateAction<Position>>,
    setEvent: React.Dispatch<React.SetStateAction<IEvent|null>>,
    setCalendar: React.Dispatch<React.SetStateAction<Day[][]>>,
    setUser: React.Dispatch<React.SetStateAction<User>>
  }) {
  const checkingBalanceRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const dow = useMemo(() => {
    return ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
  }, []);
  const [processing, setProcessing] = useState<boolean>(false);
  const [processingTotal, setProcessingTotal] = useState<boolean>(false);
  const [processingTop, setProcessingTop] = useState<number>(0);
  const selectEvent = useCallback((e: MouseEvent, event: IEvent) => {
    e.stopPropagation();
    const tooWide = e.clientX + 400 > window.innerWidth;
    const tooHigh = e.clientY + 200 > window.innerHeight;
    const eventOffset = 25;
    const x = tooWide ? window.innerWidth - 410 : e.clientX;
    const y = tooHigh ? e.clientY - 210 : e.clientY + eventOffset;
    const eventOrigin: Position = { left: x, top: y } as Position;
    setEvent(event);
    setEventOrigin(eventOrigin);
    navigate(`/event/${event.id}`, {
      state: {
        event,
        origin: eventOrigin,
        user,
      },
    });
  }, [day, user, theme]);
  const addEvent = useCallback((e: MouseEvent) => {
    setProcessing(true);
    let src: HTMLElement | null = e.target as HTMLElement;
    while (src && !src.classList.contains('total')) {
      src = src.parentElement;
    }
    if (src) return;
    const event = new Event(day, user.id);
    xhr({
      method: 'PUT',
      url: HOST + '/save-event',
      body: event
    }).then((res: ApiResponse) => {
      if (res.success) {
        setCalendar(res.data as Day[][]);
      }
      setProcessing(false);
    });
  }, [day, user, theme]);
  const blurEvent = useCallback((event: MouseEvent) => {
    setEvent(null);
    navigate('/');
  }, [day, user, theme]);
  const updateCheckingBalanceDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [totalIsInvalid, setTotalIsInvalid] = useState(false);
  const updateCheckingBalance = useCallback(() => {
    if (updateCheckingBalanceDebounceRef.current !== null) {
      clearTimeout(updateCheckingBalanceDebounceRef.current);
    }
    let checkingBalance = checkingBalanceRef.current!.value;
    if (/^0+/.test(checkingBalance)) {
      checkingBalance = checkingBalance.replace(/^0+/, '');
      let cb = document.getElementById('checking-balance') as HTMLInputElement;
      cb!.value = checkingBalance
    }
    setUser({
      ...user,
      checkingBalance: +checkingBalance
    });
    updateCheckingBalanceDebounceRef.current = setTimeout(() => {
      if (!checkingBalance) {
        setTotalIsInvalid(true);
        return;
      } else {
        setTotalIsInvalid(false);
      }
      setProcessingTotal(true);
      xhr({
        method: 'POST',
        url: HOST + '/update-checking-balance',
        body: checkingBalance
      }).then((res: ApiResponse) => {
        if (!res.error) {
          setCalendar(res.data);
        }
        setProcessingTotal(false);
      });
    }, 2000);
  }, [day, user, theme]);

  const inCurrentMonth = useMemo(() => day.month !== user.account.month, [user.account])
    

	return <div id={day.month + "-" + day.date}  className={`day-block ${theme}${inCurrentMonth ? ' opaque' : ''}${(day.dow == 0 || day.dow == 6 ? ' weekend' : '')}`} 
      key={`${a}.${b}`} 
      onClick={blurEvent} 
      onDoubleClick={addEvent}>
    <div className="day-header">
      {
        window.innerWidth < 800 ? <i className="dow">{
          dow[day.dow]
        }</i> : null
      }
      {
        day.isTodayOrLater ?
          <div className={"total" + (day.total < 0 ? ' negative' : ' positive')}>
             {
              day.isToday ? (
                <>
                  {
                    processingTotal ? null : <span style={{ color: 'black' }}>$</span>
                  }
                  {
                    processingTotal ? 
                    <div className="processing">
                      <div className="loading"></div>
                    </div> :
                    <input type="number"
                        className={totalIsInvalid ? "invalid" : ""}
                        value={ user.checkingBalance }
                        onClick={(e: React.MouseEvent<HTMLInputElement>) => e.preventDefault()}
                        onChange={updateCheckingBalance}
                        ref={checkingBalanceRef}
                        id="checking-balance"/> 
                    }
                </>
                ) : (
                  day.events.length ? <>
                    <span style={{ color: 'black' }}>$</span>
                    { day.total.toFixed(0) }
                  </> : null
                )
              }
          </div>
        : null
      }
      <i className="mobile-day-monthname">{ MONTHNAMES[day.month - 1] }</i>
      
      <div className={`day-date${day.isToday ? ' today' : ''}`}>
        { day.date }
      </div> 
    </div>
    <div className="events">
      {
        day.events.map((event: IEvent, c: number) => (
          <div className={`event
              ${event.debtId ? ' debt-payment' : ''}
              ${eventId == event.id ? ' selected' : ''}
              ${event.exclude ? ' exclude' : ''}
            `} id={`event-${ event.id }`} key={`${a}.${b}.${c}`}
              onClick={(e) => selectEvent(e, event)}>
            <span className={`${event.amount >= 0 ? 'positive' : 'negative'}`}>•</span> 
            <span className={`summary`}>{ event.summary.replace('&nbsp;', '').replace('   ', '') }</span> 
            <span className={`amount ${event.amount >= 0 ? 'positive' : 'negative'}`}>
              { event.amount }
            </span>
          </div>
        ))
      }
    </div>
  </div>
}